import { useIntl } from "react-intl";
import type { ShellUser } from "../api/types";
import { getCheckRoutes } from "../components/Router/getCheckRoutes";
import type {
  RouteConfig,
  NavigationConfig,
  SubNavigationConfig,
} from "../navigationConfig";
import { formatHref } from "./formatHref";
import {
  type RouteConfigMenu,
  type RouteConfigSubMenu,
  isRouteConfigMenu,
  type IsRouteEnabledOptions,
} from "./types";

const getSubNavigationSections = (item: RouteConfigMenu) =>
  item.menu.subRouteSections;

export const useNavigationConfigTransformer = (
  routeConfig: RouteConfig[],
  user: ShellUser | null,
  options?: IsRouteEnabledOptions
): NavigationConfig => {
  const intl = useIntl();

  const getSubPaths = (item: RouteConfigMenu): string[] =>
    item.menu.subRouteSections?.flatMap((section) =>
      section.subRoutes.map((route) => route.path)
    ) || [];

  const checkRoutes = getCheckRoutes({
    userCountry: user?.account?.country || "",
    userRights: user?.rights || [],
    ...options,
  });

  const filterSubRoutes = (
    subRouteSections?: RouteConfigSubMenu[]
  ): RouteConfigSubMenu[] | undefined =>
    subRouteSections?.reduce((acc: RouteConfigSubMenu[], section) => {
      const filteredSubRoutes = section.subRoutes.reduce(
        (subAcc: RouteConfigSubMenu["subRoutes"], subRoute) =>
          checkRoutes(subRoute) ? [...subAcc, subRoute] : subAcc,
        []
      );
      const filteredSection = {
        ...section,
        subRoutes: filteredSubRoutes,
      };
      if (!filteredSection.subRoutes.length) {
        return acc;
      }
      return [...acc, filteredSection];
    }, []);

  const getSubNavigationConfig = (
    subRouteSections?: RouteConfigSubMenu[]
  ): SubNavigationConfig[] | undefined => {
    const filteredSub = filterSubRoutes(subRouteSections);
    return filteredSub?.map((section) => ({
      sectionTitle: intl.formatMessage({ id: section.textKey }),
      subRoutes: section.subRoutes.map((subRoute) => ({
        label: subRoute.textKey && intl.formatMessage({ id: subRoute.textKey }),
        href: formatHref(subRoute.href, options?.routingParams ?? {}),
        path: subRoute.path,
        mfe: subRoute.mfe,
      })),
    }));
  };

  const getHref = (item: RouteConfigMenu) => {
    const filteredSubRoutes = filterSubRoutes(getSubNavigationSections(item));
    if (
      filteredSubRoutes &&
      !!filteredSubRoutes.length &&
      filteredSubRoutes[0]?.subRoutes[0]?.href
    ) {
      return filteredSubRoutes[0].subRoutes[0].href;
    }
    return item.menu.href;
  };

  const config: NavigationConfig = routeConfig
    .filter(isRouteConfigMenu)
    .filter(checkRoutes)
    .map((item) => ({
      href: formatHref(getHref(item), options?.routingParams ?? {}),
      key: item.key,
      mfe: item.mfe,
      path: item.path,
      subPaths: getSubPaths(item),
      position: item.menu.position,
      label: intl.formatMessage({ id: item.menu.textKey }),
      subNavigationConfig: getSubNavigationConfig(
        getSubNavigationSections(item)
      ),
      ...(item.menu.icon ? { icon: item.menu.icon } : {}),
    }));
  return config;
};
